import { CommonModule } from '@angular/common';
import { Component, HostListener } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { Router, RouterModule } from '@angular/router';
import { PermissionForStudents } from '@edtech-ui/access-role-permissions/users-permissions-constants/student-roles-permissions.enum';
import { HasPermissionDirective } from '@edtech-ui/access-role-permissions/users-permissions-directives/has-permissions.directives';
import { InterviewEvaluationResponse } from '@edtech-ui/core/models/ai-interview/ai-interview-evaluation';
import { AiInterviewSessionService } from '@edtech-ui/core/services/ai-interview-session-service/ai-interview-session.service';
import { AuthServiceService } from '@edtech-ui/core/services/authService/auth-service.service';
import { ToastrService } from 'ngx-toastr';

@Component({
  selector: 'app-ai-interview-performance',
  standalone: true,
  imports: [CommonModule, FormsModule, RouterModule,HasPermissionDirective],
  templateUrl: './ai-interview-performance.component.html',
  styleUrl: './ai-interview-performance.component.scss'
})
export class AiInterviewPerformanceComponent {
  evaluationData?: InterviewEvaluationResponse['Data'];
  loading = true;
  errorMessage = '';

  totalScore = 0;
  feedbackLabel = '';
  role = '';
  company = '';
  round = '';
  duration = 0;


  // UI States
  performanceData: any[] = [];
  selectedQuestion = 0;
  activeSubTab = 'feedback';
  strengths: string[] = [];
  focusAreas: string[] = [];
  nextSteps: { color: string; text: string }[] = [];
  questions: any[] = [];
  navigationBlocked = true;
  isInterviewIncomplete = false;
  isPaid: boolean = false;
 PermissionForStudents = PermissionForStudents;
 hasInterviewResultPermission: boolean = false;

  constructor(
    private aiService: AiInterviewSessionService,
    private toast: ToastrService,
    private router: Router,
    private _authService: AuthServiceService
  ) { }

  ngOnInit() {

        this.hasInterviewResultPermission = this._authService.hasPermission(PermissionForStudents.INTERVIEW_VIEW_RESULTS);

        if (!this.hasInterviewResultPermission) {
          this.toast.warning('You do not have permission to view interview results');
          this.router.navigate(['/student-dashboard/ai-interview-dashboard']);
          return;
        }
        this.loadEvaluation();
      }

  canExitPerformance(): boolean {
    return !this.navigationBlocked;
  }

  loadEvaluation() {
    this.loading = true;

    const cachedData = localStorage.getItem('last_evaluation');
    if (cachedData) {
      try {
        this.evaluationData = JSON.parse(cachedData);

        this.isPaid = this.evaluationData?.is_paid ?? false;

        if (this.evaluationData) {
          this.mapEvaluationData(this.evaluationData);
          this.toast.success('Your interview report has been generated successfully');
        }
        this.loading = false;
        return;
      } catch (err) {
      }
    }

    const state = this.aiService.value;
    const sessionId = state.session_id || localStorage.getItem('last_session_id');

    if (!sessionId) {
      this.toast.warning('No active session found. Redirecting...');
      this.router.navigate(['/student-dashboard']);
      this.loading = false;
      return;
    }

    this.aiService.evaluateInterview(sessionId).subscribe({
      next: (res) => {
        const payload = (res as any).Data ?? (res as any).data;

        if ((res as any).Success && payload) {
          this.evaluationData = payload;
          this.toast.success('Performance report loaded!');

          this.isPaid = this.evaluationData?.is_paid ?? false;

          if (this.evaluationData) {
            this.mapEvaluationData(this.evaluationData);
          }

          localStorage.setItem('last_evaluation', JSON.stringify(payload));
          localStorage.setItem('last_session_id', sessionId);
        }

        this.loading = false;
      },
      error: (err) => {
        this.toast.error('Failed to fetch evaluation. Please try again.');
        this.loading = false;
      },
    });
  }

  private mapEvaluationData(data: InterviewEvaluationResponse['Data']) {

    this.role = data.interview_context.role;
    this.company = data.interview_context.company;
    this.round = data.interview_context.interview_round;
    this.duration = data.interview_context.duration_minutes;

    this.totalScore = data.overall_evaluation.total_score;
    this.feedbackLabel = data.overall_evaluation.feedback_label;

    const perf = data.overall_evaluation.performance_breakdown ?? {};
    this.performanceData = [
      { title: 'Communication', subtitle: 'Clarity and articulation', score: perf.communication ?? 0 },
      { title: 'Technical Knowledge', subtitle: 'Subject matter expertise', score: perf.technical_knowledge ?? 0 },
      { title: 'Confidence', subtitle: 'Self-assurance and poise', score: perf.confidence ?? 0 },
      { title: 'Structure', subtitle: 'Organized responses', score: perf.structure ?? 0 }
    ];

    this.isInterviewIncomplete = this.totalScore === 0;

    if (this.isInterviewIncomplete) {
      this.questions = [];
      this.strengths = [];

      this.feedbackLabel = 'Interview Incomplete';

      this.focusAreas = data.overall_evaluation?.focus_areas?.length
        ? data.overall_evaluation.focus_areas :
        [
          'Complete the full interview to receive a proper evaluation',
          'Ensure you are prepared before starting the interview',
          'Allocate sufficient uninterrupted time for the interview session',
        ];

      this.mapRecommendedSteps(data);
      return;
    }


    // Questions
    this.questions = data.questions.map((q) => ({
      question: q.question,
      score: q.score,
      answer: q.user_answer,
      good: q.what_went_well,
      improve: q.areas_to_improve,
      improved: q.improved_answer,
      tip: 'Reflect on detailed feedback for improvement.',
      better: `Score: ${q.score}/10 - ${q.feedback_label}`
    }));

    // Summary
    this.strengths = data.overall_evaluation?.key_strengths ?? [];

    this.focusAreas = data.overall_evaluation?.focus_areas?.length
      ? data.overall_evaluation.focus_areas
      : ['No focus areas available at this time.'];

    this.mapRecommendedSteps(data);
  }

  private mapRecommendedSteps(data: InterviewEvaluationResponse['Data']) {
    const steps = data.overall_evaluation?.recommended_next_steps ?? [];

    const colors = ['purple', 'blue', 'green', 'orange', 'red'];

    this.nextSteps = steps.length
      ? steps.map((step: string, index: number) => ({
        color: colors[index % colors.length],
        text: step
      }))
      : [
        { color: 'gray', text: 'No recommended steps available yet.' }
      ];
  }


  selectQuestion(index: number) {
    this.selectedQuestion = index;
    this.activeSubTab = 'feedback';
  }

  getScoreClass(score: number) {
    if (score >= 8) return 'excellent';
    if (score >= 5) return 'average';
    return 'Needs-improvement';
  }

  getRatingText(score: number): string {
    if (score >= 8) return 'Excellent';
    if (score >= 5) return 'Good';
    return 'Needs Improvement';
  }

  getProgressAngle(score: number): string {
    const percent = Math.min(Math.max(score, 0), 100);
    const degrees = (percent / 100) * 360;
    return `${degrees}deg`;
  }

  get displayScore(): number {
    return Math.floor(this.totalScore || 0);
  }

  getOneDecimalScore(score: number | null | undefined): string {
    if (score == null) return '0.0';
    return (Math.floor(score * 10) / 10).toFixed(1);
  }

  getScorePercent(score: number | null | undefined): number {
    return Math.floor((score || 0) * 10);
  }

  goback() {
    this.navigationBlocked = false;

    this.router.navigate(['/student-dashboard/ai-interview-dashboard']);
  }

  goToUpgradePage() {
    this.navigationBlocked = false;
    this.router.navigate(['/student-dashboard/upgrade-plan'])
  }

}
